import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

interface ParsedValue {
  prefix: string;
  target: number;
  suffix: string;
  fractionDigits: number;
}

// Split a numeric KPI value string like "$1,019", "26.5%", "14", "23 yr" into
// an animatable number plus the surrounding prefix/suffix so the count-up can
// re-render it with the same shape (thousands separators, decimals, % sign,
// unit). The prefix is restricted to a sign / currency glyph so identifiers
// that merely START with letters+digits — "BOS-0303", "A-01", "T5" — fail to
// match and are returned verbatim (never animated as numbers).
//
// Accepts non-string inputs defensively: the streaming gate is the primary
// guard against partial-JSON items with missing `value`, but an agent contract
// violation (final spec with item.value === undefined) would otherwise crash
// the renderer here. Returning null degrades to "display the value verbatim",
// which for undefined renders as nothing — fail-soft beats fail-loud.
function parseValue(value: string | undefined | null): ParsedValue | null {
  if (typeof value !== "string" || value.length === 0) return null;
  const m = value.match(/^([$€£¥+-]?\s?)([\d,]+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  const [, prefix, numRaw, suffix] = m;
  const target = Number(numRaw.replace(/,/g, ""));
  if (!Number.isFinite(target)) return null;
  const dot = numRaw.indexOf(".");
  const fractionDigits = dot === -1 ? 0 : numRaw.length - dot - 1;
  return { prefix, target, suffix, fractionDigits };
}

const DURATION_MS = 600;

/**
 * Animate a KPI value string from 0 up to its number on mount. Because KPI
 * sections only mount once their data has committed (the stream's
 * isRenderableSection gate holds them back), "on mount" === "after commit" —
 * the count-up never runs against half-streamed numbers (B4 rule 7).
 *
 * Non-numeric values and reduced-motion users get the final string immediately.
 */
export function useCountUp(value: string | undefined | null): string {
  const reduce = useReducedMotion();
  const safeValue = typeof value === "string" ? value : "";
  const parsed = parseValue(safeValue);
  const [display, setDisplay] = useState(() =>
    parsed && !reduce ? format(0, parsed) : safeValue,
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!parsed || reduce) {
      setDisplay(safeValue);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(format(parsed.target * eased, parsed));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(safeValue); // land on the exact original string
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // value is the trigger; re-running on a new committed value is intended.
  }, [value, reduce]); // eslint-disable-line react-hooks/exhaustive-deps

  return display;
}

function format(n: number, p: ParsedValue): string {
  const num = n.toLocaleString(undefined, {
    minimumFractionDigits: p.fractionDigits,
    maximumFractionDigits: p.fractionDigits,
  });
  return `${p.prefix}${num}${p.suffix}`;
}
